import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import {
  Buildings, IdentificationCard, Bank, SignatureIcon, Hash, Gear, WhatsappLogo, FileText,
  Check, Eye, ArrowClockwise, UploadSimple, X, Warning
} from "@phosphor-icons/react";

const TABS = [
  { key: "profile", label: "Company Profile", icon: Buildings },
  { key: "statutory", label: "Statutory / GST", icon: IdentificationCard },
  { key: "bank", label: "Bank & Payment", icon: Bank },
  { key: "signature", label: "Logo · Signature · Seal", icon: SignatureIcon },
  { key: "numbering", label: "Document Numbering", icon: Hash },
  { key: "defaults", label: "Doc Defaults", icon: FileText },
  { key: "template", label: "Template Style", icon: Gear },
  { key: "whatsapp", label: "WhatsApp Templates", icon: WhatsappLogo },
];

export default function AdminSettings({ user, logout }) {
  const role = user?.role || "";
  const canEdit = role === "admin";
  const canView = ["admin", "manager", "bdm", "cfo"].includes(role);

  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [numPreview, setNumPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/company-settings`);
      setForm(data);
      setDirty(false);
    } catch { toast.error("Failed to load settings"); }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!canView) return;
    try {
      const { data } = await axios.get(`${API}/company-settings/numbering-preview`, {
        params: { requester_role: role },
      });
      setNumPreview(data);
    } catch { /* ignore */ }
  }, [canView, role]);

  useEffect(() => { if (canView) load(); }, [canView, load]);
  useEffect(() => { if (activeTab === "numbering") loadPreview(); }, [activeTab, loadPreview]);

  const update = (patch) => { setForm(f => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await axios.put(`${API}/company-settings?requester_role=admin`, form);
      toast.success("Settings saved. New values apply to all future documents.");
      setDirty(false);
      // Invalidate WA helper cache
      try { const h = await import("../utils/whatsappHelper"); h.invalidateCompanySettings(); } catch { /* ignore */ }
      try { const o = await import("../utils/waOpener"); o.invalidateWhatsappOpenMode(); } catch { /* ignore */ }
      loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const resetCounter = async (docType) => {
    if (!canEdit) return;
    const start = window.prompt(`Reset ${docType} counter to what number? (e.g. 0 to restart from 0001)`, "0");
    if (start === null) return;
    try {
      await axios.post(`${API}/company-settings/reset-counter?requester_role=admin`,
        { doc_type: docType, start_from: parseInt(start) || 0 });
      toast.success(`${docType} counter reset`);
      loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const uploadImage = (key, maxKB = 300) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxKB * 1024) { toast.error(`Image larger than ${maxKB}KB`); return; }
    const reader = new FileReader();
    reader.onload = () => update({ [key]: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12">
          <div className="bg-white border border-amber-200 p-8 text-center max-w-md mx-auto">
            <Warning size={28} className="mx-auto text-amber-500 mb-3" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Access Restricted</h2>
            <p className="text-sm text-slate-600">Company Settings are visible to Admin, Manager, BDM, and CFO only.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12 text-center text-slate-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20 flex items-center justify-between">
          <div>
            <div className="flex items-center"><h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Company Settings</h1><HelpButton moduleKey="admin_settings" /></div>
            <p className="text-xs text-slate-500 mt-0.5">
              Everything that appears on your invoices, quotations, receipts, and PDFs — edit here, save once, applies everywhere.
              {!canEdit && <span className="text-amber-700 font-bold ml-2">(Read-only for your role)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-[11px] bg-amber-100 border border-amber-300 text-amber-800 px-2 py-1 font-bold">Unsaved changes</span>}
            <button onClick={load} data-testid="reload-btn" className="text-xs border border-slate-300 hover:bg-slate-50 px-3 py-2 font-bold flex items-center gap-1.5">
              <ArrowClockwise size={12} /> Reload
            </button>
            <button onClick={save} disabled={!canEdit || !dirty || saving} data-testid="save-settings-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
              <Check size={14} weight="bold" /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="flex">
          {/* Left tab rail */}
          <aside className="w-56 bg-white border-r border-slate-200 min-h-[calc(100vh-64px)]">
            <nav className="py-2">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = activeTab === t.key;
                return (
                  <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setActiveTab(t.key)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm border-l-4 transition-colors ${active ? "bg-blue-50 border-[#002FA7] text-[#002FA7] font-bold" : "border-transparent text-slate-600 hover:bg-slate-50"}`}>
                    <Icon size={15} weight={active ? "fill" : "regular"} />
                    {t.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Right content */}
          <main className="flex-1 p-6">
            {activeTab === "profile" && <ProfileTab form={form} update={update} canEdit={canEdit} />}
            {activeTab === "statutory" && <StatutoryTab form={form} update={update} canEdit={canEdit} />}
            {activeTab === "bank" && <BankTab form={form} update={update} canEdit={canEdit} uploadImage={uploadImage} />}
            {activeTab === "signature" && <SignatureTab form={form} update={update} canEdit={canEdit} uploadImage={uploadImage} />}
            {activeTab === "numbering" && <NumberingTab form={form} update={update} canEdit={canEdit} preview={numPreview} resetCounter={resetCounter} />}
            {activeTab === "defaults" && <DefaultsTab form={form} update={update} canEdit={canEdit} />}
            {activeTab === "template" && <TemplateTab form={form} update={update} canEdit={canEdit} />}
            {activeTab === "whatsapp" && <WhatsAppTab form={form} update={update} canEdit={canEdit} />}
          </main>
        </div>
      </div>
    </div>
  );
}

/* ========== Shared field helpers ========== */
const Field = ({ label, hint, children, full = false, required = false }) => (
  <div className={full ? "col-span-2" : ""}>
    <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, desc, children }) => (
  <section className="bg-white border border-slate-200 mb-5">
    <div className="px-5 py-3 border-b border-slate-100">
      <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
      {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const inp = "w-full border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none disabled:bg-slate-50 rounded-none";

/* ========== TABS ========== */

function ProfileTab({ form, update, canEdit }) {
  const f = (key, label, opts = {}) => (
    <Field label={label} hint={opts.hint} full={opts.full} required={opts.required}>
      {opts.textarea ? (
        <textarea value={form[key] || ""} disabled={!canEdit} rows={opts.rows || 2}
          onChange={e => update({ [key]: e.target.value })} data-testid={`cs-${key}`} className={inp} />
      ) : (
        <input type={opts.type || "text"} value={form[key] || ""} disabled={!canEdit}
          onChange={e => update({ [key]: e.target.value })} data-testid={`cs-${key}`} className={inp} />
      )}
    </Field>
  );

  return (
    <>
      <Section title="Brand Identity" desc="How your company name appears across all documents">
        <div className="grid grid-cols-2 gap-4">
          {f("company_name", "Company Name (display)", { required: true })}
          {f("legal_name", "Legal Entity Name", { hint: "Full registered name — e.g. SwifTech India Pvt. Ltd." })}
          {f("tagline", "Tagline", { full: true })}
        </div>
      </Section>

      <Section title="Registered Address" desc="Printed on invoices and quotations as seller's address">
        <div className="grid grid-cols-2 gap-4">
          {f("address_line1", "Address Line 1", { full: true })}
          {f("address_line2", "Address Line 2", { full: true })}
          {f("city", "City")}
          {f("state", "State")}
          {f("pincode", "Pincode")}
          {f("country", "Country")}
        </div>
      </Section>

      <Section title="Contact" desc="Mobile/WhatsApp shown on PDFs and the public product catalogue">
        <div className="grid grid-cols-2 gap-4">
          {f("phone", "Landline")}
          {f("mobile", "Mobile")}
          {f("email", "Email", { type: "email" })}
          {f("website", "Website")}
        </div>
      </Section>
    </>
  );
}

function StatutoryTab({ form, update, canEdit }) {
  const f = (key, label, opts = {}) => (
    <Field label={label} hint={opts.hint} required={opts.required}>
      <input type={opts.type || "text"} value={form[key] ?? ""} disabled={!canEdit}
        onChange={e => update({ [key]: opts.type === "number" ? Number(e.target.value) : e.target.value.toUpperCase() })}
        data-testid={`cs-${key}`} className={`${inp} font-mono uppercase`} />
    </Field>
  );
  return (
    <>
      <Section title="GST & Tax Identity" desc="Your seller identity on every tax invoice. The GSTIN appears on all documents — get it right.">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Seller GSTIN" required hint="15-character GST number. Format: 22AAAAA0000A1Z5">
            <input value={form.gstin || ""} disabled={!canEdit} maxLength={15}
              onChange={e => update({ gstin: e.target.value.toUpperCase() })}
              data-testid="cs-gstin" className={`${inp} font-mono uppercase tracking-wider text-base`}
              placeholder="22AAAAA0000A1Z5" />
            {form.gstin && form.gstin.length !== 15 && (
              <p className="text-[10px] text-amber-700 mt-1">GSTIN must be exactly 15 characters.</p>
            )}
          </Field>
          {f("state_code", "GST State Code (2-digit)", { hint: "Auto-extracted from first 2 digits of GSTIN" })}
          {f("pan", "PAN Number", { hint: "10 characters. Format: ABCDE1234F" })}
          {f("cin", "CIN Number", { hint: "Corporate Identification Number (for private limited companies)" })}
          <Field label="Default GST Rate %" hint="Used when a product doesn't specify its own rate">
            <input type="number" min="0" max="28" step="0.5" value={form.default_gst_rate ?? 18}
              disabled={!canEdit} onChange={e => update({ default_gst_rate: Number(e.target.value) })}
              data-testid="cs-default_gst_rate" className={inp} />
          </Field>
        </div>
      </Section>
    </>
  );
}

function BankTab({ form, update, canEdit, uploadImage }) {
  const f = (key, label, opts = {}) => (
    <Field label={label} hint={opts.hint}>
      <input type={opts.type || "text"} value={form[key] || ""} disabled={!canEdit}
        onChange={e => update({ [key]: e.target.value })} data-testid={`cs-${key}`}
        className={`${inp} ${opts.mono ? "font-mono" : ""}`} />
    </Field>
  );
  return (
    <>
      <Section title="Bank Account" desc="Printed on every invoice for cheque/NEFT payments">
        <div className="grid grid-cols-2 gap-4">
          {f("bank_name", "Bank Name")}
          {f("branch_name", "Branch Name")}
          {f("account_holder_name", "Account Holder Name")}
          {f("account_number", "Account Number", { mono: true })}
          {f("ifsc", "IFSC Code", { mono: true })}
          {f("swift_code", "SWIFT Code (international)", { mono: true })}
        </div>
      </Section>

      <Section title="UPI / QR" desc="For instant payments — appears as a QR code on invoice PDFs when enabled">
        <div className="grid grid-cols-2 gap-4">
          <Field label="UPI ID" hint="Format: name@bank (e.g. swiftech@hdfcbank, swiftech@oksbi, swiftech@paytm)">
            <input type="text" value={form.upi_id || ""} disabled={!canEdit}
              onChange={e => update({ upi_id: e.target.value.trim().toLowerCase() })}
              placeholder="yourbusiness@hdfcbank"
              data-testid="cs-upi_id" className={`${inp} font-mono`} />
            <UpiHealth upi={form.upi_id} />
          </Field>
          <Field label="UPI QR Image (optional, auto-generated if blank)">
            <div className="flex items-center gap-3">
              {form.upi_qr_url && <img src={form.upi_qr_url} alt="UPI QR" className="w-16 h-16 object-contain border border-slate-200 bg-white p-1" />}
              <label className={`border border-slate-300 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 font-bold ${!canEdit ? "opacity-50 pointer-events-none" : ""}`}>
                <UploadSimple size={12} className="inline mr-1" /> Upload QR
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage("upi_qr_url", 300)} data-testid="cs-upi_qr_url-upload" />
              </label>
              {form.upi_qr_url && canEdit && <button onClick={() => update({ upi_qr_url: "" })} className="text-xs text-rose-600 hover:underline"><X size={10} className="inline" /> Clear</button>}
            </div>
          </Field>
        </div>
        <UpiTester upi={form.upi_id} companyName={form.company_name || form.legal_name} />
      </Section>
    </>
  );
}

/* UPI ID format validator — show inline warnings */
function UpiHealth({ upi }) {
  if (!upi) return null;
  const issues = [];
  if (!upi.includes("@")) issues.push("Missing '@handle' — UPI must be like name@hdfcbank");
  else {
    const [name, handle] = upi.split("@");
    if (!name || name.length < 2) issues.push("Name part before '@' is too short");
    if (!handle || !/^[a-z]{3,20}$/.test(handle)) issues.push("Bank handle should be lowercase letters (e.g. hdfcbank, oksbi, paytm, ybl)");
    if (/[A-Z]/.test(upi)) issues.push("Use lowercase only — UPI IDs are case-sensitive in some apps");
    if (/\s/.test(upi)) issues.push("Spaces not allowed");
  }
  if (issues.length === 0) {
    return <p className="text-[10px] text-emerald-700 mt-1">✓ Format looks valid</p>;
  }
  return (
    <div className="text-[10px] text-amber-700 mt-1 space-y-0.5">
      {issues.map((i, idx) => <div key={idx}>⚠ {i}</div>)}
    </div>
  );
}

/* "Test your UPI" widget — shows a scannable QR for the admin to verify with their own phone */
function UpiTester({ upi, companyName }) {
  const [open, setOpen] = useState(false);
  if (!upi || !upi.includes("@")) return null;
  const testAmount = 1;
  const upiUrl = `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(companyName || "SwifTech")}&am=${testAmount}&cu=INR&tn=TEST-UPI-VERIFICATION`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(upiUrl)}`;
  return (
    <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3" data-testid="upi-tester-card">
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs text-amber-900">
          <b>⚡ Verify your UPI ID is accepting payments</b>
          <p className="mt-1 text-amber-800">If a customer reports "<i>receiver is not accepting payments on this specific UPI ID</i>", click below — scan with your own UPI app, send ₹1 to yourself. If your bank accepts it, customers will too.</p>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="upi-test-btn"
          className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 shrink-0">
          {open ? "Hide" : "Test UPI"}
        </button>
      </div>
      {open && (
        <div className="mt-3 flex flex-col items-center gap-2 bg-white p-3 border border-amber-200">
          <img src={qrUrl} alt="Test UPI QR" className="w-44 h-44" />
          <div className="text-[10px] font-mono text-slate-600 break-all max-w-md text-center">{upi}</div>
          <div className="text-[11px] text-slate-700">
            <a href={upiUrl} className="text-[#002FA7] underline font-bold">Tap to open on this device</a>
            {" or scan with another phone"}
          </div>
          <ul className="text-[10px] text-slate-600 list-disc pl-4 mt-1 space-y-0.5">
            <li>If you see <b>"Pay ₹1 to YOUR_NAME"</b> → ✅ UPI is healthy</li>
            <li>If you see <b>"Receiver not accepting payments"</b> → ❌ contact your bank, or use a different UPI</li>
            <li>If app says <b>"Invalid UPI ID"</b> → ❌ typo in UPI ID, fix above</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function SignatureTab({ form, update, canEdit, uploadImage }) {
  const imgField = (key, label, hint, maxKB = 250) => (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        {form[key] && <img src={form[key]} alt="" className="w-20 h-20 object-contain border border-slate-200 bg-white p-1" />}
        <label className={`border border-slate-300 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 font-bold ${!canEdit ? "opacity-50 pointer-events-none" : ""}`}>
          <UploadSimple size={12} className="inline mr-1" /> Upload
          <input type="file" accept="image/*" className="hidden" onChange={uploadImage(key, maxKB)} data-testid={`cs-${key}-upload`} />
        </label>
        {form[key] && canEdit && <button onClick={() => update({ [key]: "" })} className="text-xs text-rose-600 hover:underline"><X size={10} className="inline" /> Clear</button>}
      </div>
    </Field>
  );
  return (
    <>
      <Section title="Logo" desc="Shown at top-left of every printed document">
        {imgField("logo_url", "Company Logo", "Recommended 200×200 px, transparent PNG, < 250 KB", 250)}
      </Section>
      <Section title="Authorised Signatory" desc="Printed at bottom-right of invoices/quotations">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Signatory Name">
            <input value={form.signature_name || ""} disabled={!canEdit}
              onChange={e => update({ signature_name: e.target.value })} data-testid="cs-signature_name" className={inp} />
          </Field>
          <Field label="Signatory Title" hint="e.g. Director, Proprietor, Authorised Representative">
            <input value={form.signature_title || ""} disabled={!canEdit}
              onChange={e => update({ signature_title: e.target.value })} data-testid="cs-signature_title" className={inp} />
          </Field>
          {imgField("signature_url", "Signature Image (optional)", "Scanned handwritten signature on white background, < 150 KB", 150)}
          {imgField("seal_url", "Company Seal / Stamp (optional)", "Round/rectangular company stamp image, < 250 KB", 250)}
        </div>
      </Section>
    </>
  );
}

function NumberingTab({ form, update, canEdit, preview, resetCounter }) {
  const p = (key, label, docType) => (
    <div className="flex items-end gap-2">
      <Field label={label}>
        <input value={form[key] || ""} disabled={!canEdit} maxLength={10}
          onChange={e => update({ [key]: e.target.value.toUpperCase() })}
          data-testid={`cs-${key}`} className={`${inp} font-mono uppercase`} />
      </Field>
      <div className="mb-0.5 shrink-0">
        <div className="text-[10px] text-slate-500 mb-0.5">Next will be</div>
        <code className="block text-[11px] bg-blue-50 border border-blue-200 text-[#002FA7] px-2 py-1 font-mono font-bold">
          {preview?.[docType] || "—"}
        </code>
      </div>
      {canEdit && (
        <button onClick={() => resetCounter(docType)} data-testid={`reset-${docType}`} title={`Reset ${docType} counter`}
          className="mb-0.5 border border-amber-300 text-amber-700 hover:bg-amber-50 p-1.5 rounded-none"><ArrowClockwise size={12} /></button>
      )}
    </div>
  );
  return (
    <>
      <Section title="Document Number Prefixes" desc="Each document type gets a sequential number. Change the prefix or reset the counter anytime.">
        <div className="grid grid-cols-2 gap-4">
          {p("estimation_prefix", "Estimation Prefix", "estimation")}
          {p("quotation_prefix", "Quotation Prefix", "quotation")}
          {p("proforma_prefix", "Proforma Prefix", "proforma")}
          {p("invoice_prefix", "Invoice Prefix", "invoice")}
          {p("sales_order_prefix", "Sales Order Prefix", "sales_order")}
          {p("delivery_challan_prefix", "Delivery Challan Prefix", "delivery_challan")}
          {p("receipt_prefix", "Receipt Prefix", "receipt")}
          {p("credit_note_prefix", "Credit Note Prefix", "credit_note")}
          {p("debit_note_prefix", "Debit Note Prefix", "debit_note")}
        </div>
      </Section>
      <Section title="Financial Year" desc="Documents can auto-embed the financial year (e.g. INV-2526-0001). Set 0 to disable.">
        <Field label="Financial Year Starts in Month" hint="Indian FY: 4 (April). Calendar year: 1 (January). Set 0 to disable FY embedding in numbers.">
          <select value={form.financial_year_start_month ?? 4} disabled={!canEdit}
            onChange={e => update({ financial_year_start_month: Number(e.target.value) })}
            data-testid="cs-financial_year_start_month" className={inp}>
            <option value={0}>0 — Do not embed FY in numbers</option>
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
              <option key={m} value={i + 1}>{i + 1} — {m}</option>
            ))}
          </select>
        </Field>
      </Section>
    </>
  );
}

function DefaultsTab({ form, update, canEdit }) {
  const f = (key, label, opts = {}) => (
    <Field label={label} hint={opts.hint} full={opts.full}>
      {opts.textarea ? (
        <textarea value={form[key] || ""} disabled={!canEdit} rows={opts.rows || 4}
          onChange={e => update({ [key]: e.target.value })} data-testid={`cs-${key}`} className={inp} />
      ) : (
        <input type={opts.type || "text"} value={form[key] ?? ""} disabled={!canEdit}
          onChange={e => update({ [key]: opts.type === "number" ? Number(e.target.value) : e.target.value })}
          data-testid={`cs-${key}`} className={inp} />
      )}
    </Field>
  );
  return (
    <>
      <Section title="Default Terms applied to every new document">
        <div className="grid grid-cols-2 gap-4">
          {f("default_quotation_validity_days", "Quotation Validity (days)", { type: "number", hint: "Auto-set expiry_date = today + this many days" })}
          {f("default_payment_terms", "Default Payment Terms", { textarea: true, full: true, rows: 2 })}
          {f("default_warranty_terms", "Default Warranty Terms", { textarea: true, full: true, rows: 2 })}
          {f("terms", "Terms & Conditions (generic)", { textarea: true, full: true, rows: 4 })}
          {f("default_notes", "Default Notes / Thank-You Line", { textarea: true, full: true, rows: 2 })}
        </div>
      </Section>
    </>
  );
}

function TemplateTab({ form, update, canEdit }) {
  const ck = (key, label) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={form[key] !== false} disabled={!canEdit}
        onChange={e => update({ [key]: e.target.checked })} data-testid={`cs-${key}`}
        className="w-4 h-4 accent-[#002FA7]" />
      {label}
    </label>
  );
  return (
    <Section title="PDF / Invoice Appearance" desc="Live preview of these changes is visible on any document you create after saving.">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Accent Color" hint="Used for headers, totals row, and brand accents on PDFs">
          <div className="flex items-center gap-2">
            <input type="color" value={form.template_accent_color || "#002FA7"} disabled={!canEdit}
              onChange={e => update({ template_accent_color: e.target.value })}
              data-testid="cs-template_accent_color" className="h-10 w-14 rounded-none border border-slate-300 cursor-pointer" />
            <input type="text" value={form.template_accent_color || "#002FA7"} disabled={!canEdit}
              onChange={e => update({ template_accent_color: e.target.value })} className={`${inp} font-mono`} />
          </div>
        </Field>
        <Field label="Template Style">
          <select value={form.template_style || "standard"} disabled={!canEdit}
            onChange={e => update({ template_style: e.target.value })} data-testid="cs-template_style" className={inp}>
            <option value="standard">Standard (Zoho-style)</option>
            <option value="compact">Compact (less whitespace)</option>
            <option value="classic">Classic (traditional Indian GST)</option>
          </select>
        </Field>
        {ck("template_show_hsn", "Show HSN / SAC column")}
        {ck("template_show_discount", "Show Discount column")}
        {ck("template_show_bank", "Show Bank Details block")}
        {ck("template_show_signature", "Show Signature block")}
        {ck("template_show_amount_words", "Show Amount-in-words line")}
        {ck("template_show_qr", "Show UPI QR code")}
        <Field label="Footer text (below signature)" full>
          <textarea value={form.template_footer || ""} disabled={!canEdit} rows={2}
            onChange={e => update({ template_footer: e.target.value })}
            placeholder="e.g. Subject to Mumbai jurisdiction. All disputes to be resolved at Mumbai."
            data-testid="cs-template_footer" className={inp} />
        </Field>
      </div>
    </Section>
  );
}

function WhatsAppTab({ form, update, canEdit }) {
  return (
    <Section title="WhatsApp Templates" desc="Tokens supported: {product} {model} {quantity} {name}">
      <div className="grid grid-cols-1 gap-4">
        <Field label="Company WhatsApp Number" hint="Country code + number, digits only. e.g. 919876543210">
          <input value={form.whatsapp_number || ""} disabled={!canEdit}
            onChange={e => update({ whatsapp_number: e.target.value.replace(/[^\d]/g, "") })}
            data-testid="cs-whatsapp_number" className={`${inp} font-mono`} />
        </Field>
        <Field label="Customer Enquiry Template (sent from catalogue)">
          <textarea value={form.whatsapp_enquiry_template || ""} disabled={!canEdit} rows={2}
            onChange={e => update({ whatsapp_enquiry_template: e.target.value })}
            data-testid="cs-whatsapp_enquiry_template" className={inp} />
        </Field>
        <Field label="Lead Follow-up Template (sent from Leads drawer)">
          <textarea value={form.whatsapp_followup_template || ""} disabled={!canEdit} rows={2}
            onChange={e => update({ whatsapp_followup_template: e.target.value })}
            data-testid="cs-whatsapp_followup_template" className={inp} />
        </Field>
        <Field label="WhatsApp Open Mode" hint="Desktop only. New tab = recommended (preserves your work). Mobile always opens the WhatsApp app.">
          <select value={form.whatsapp_open_mode || "new_tab"} disabled={!canEdit}
            onChange={e => update({ whatsapp_open_mode: e.target.value })}
            data-testid="cs-whatsapp_open_mode" className={inp}>
            <option value="new_tab">Open in new tab (recommended)</option>
            <option value="same_tab">Open in same tab</option>
          </select>
        </Field>
      </div>
    </Section>
  );
}
